import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Each entry: [label, path, settingKey]
const NAV_LINKS = [
  ['Features',     '/features',        'nav_features'    ],
  ['Browse roles', '/roles',           'nav_browse_roles'],
  ['JD Prep',      '/jd-prep',         'nav_jd_prep'     ],
  ['ATS Score',    '/ats',             'nav_ats_score'   ],
  ['Study Guides', '/courses',         'nav_study_guides'],
  ['Search Jobs',  '/careers',         'nav_search_jobs' ],
  ['Blog',         '/blog',            'nav_blog'        ],
  ['Pricing',      '/pricing',         'nav_pricing'     ],
];

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEFAULT_NAV = Object.fromEntries(NAV_LINKS.map(([,, k]) => [k, true]));

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function IconFeatures()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>; }
function IconRoles()    { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>; }
function IconJDPrep()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>; }
function IconATS()      { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>; }
function IconGuides()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>; }
function IconJobs()     { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function IconBlog()     { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>; }
function IconPricing()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>; }

const LINK_ICONS = {
  'Features':     <IconFeatures />,
  'Browse roles': <IconRoles />,
  'JD Prep':      <IconJDPrep />,
  'ATS Score':    <IconATS />,
  'Study Guides': <IconGuides />,
  'Search Jobs':  <IconJobs />,
  'Blog':         <IconBlog />,
  'Pricing':      <IconPricing />,
};

/**
 * Floating glass navbar.
 * Props:
 *   forceLight — always render the light style (for white-bg pages).
 *                When false, starts transparent over dark heroes and transitions on scroll.
 */
export default function PublicNavbar({ forceLight = false }) {
  const location = useLocation();
  const { user } = useAuth();
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [navVis,    setNavVis]    = useState(DEFAULT_NAV);
  const menuRef = useRef(null);

  // Scroll detection
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn(); // run once on mount
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Load nav visibility from admin settings
  useEffect(() => {
    fetch(`${BASE}/system/nav`)
      .then(r => r.json())
      .then(d => setNavVis({ ...DEFAULT_NAV, ...d }))
      .catch(() => {});
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Outside-click handled by the backdrop div in the dropdown — no mousedown listener needed.

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isLight = forceLight || scrolled;
  const visibleLinks = NAV_LINKS.filter(([,, key]) => navVis[key] !== false);

  return (
    <>
      <style>{`
        .pub-nav-link{transition:color 0.18s ease}
        .pub-nav-link:hover{color:#1B3A5C !important}
        .pub-nav-link-dark:hover{color:#ffffff !important}
        .pub-nav-link-red:hover{color:#A8202B !important}
        .pub-cta-btn:hover{background:#A8202B !important;transform:translateY(-1px);box-shadow:0 4px 16px rgba(193,39,45,0.3)}
        .pub-cta-btn{transition:background 0.18s ease,transform 0.18s ease,box-shadow 0.18s ease}
        .pub-outline-btn:hover{background:rgba(27,58,92,0.08) !important}
        .pub-outline-btn-dark:hover{background:rgba(255,255,255,0.12) !important}
        .pub-mobile-link{transition:color 0.15s,background 0.15s;border-radius:10px}
        .pub-mobile-link:hover{background:#F1F5F9;color:#1B3A5C !important}
        @keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* No skip link here — App.jsx already renders one unconditionally at
          the app root, present on every route regardless of which navbar
          (if any) a page uses. This component used to have its own,
          redundant one (found live 2026-07-10 while checking a course page:
          two "Skip to main content" links back to back for keyboard users,
          on every page using PublicNavbar — harmless but worth removing). */}

      {/* ── Floating navbar ── */}
      <nav
        ref={menuRef}
        style={{
          position: 'fixed',
          top: isLight ? 0 : 12,
          left: isLight ? 0 : 12,
          right: isLight ? 0 : 12,
          zIndex: 100,
          borderRadius: isLight ? 0 : 16,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: isLight
            ? 'rgba(255,255,255,0.96)'
            : 'rgba(4,13,24,0.55)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isLight
            ? '0 1px 0 rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.06)'
            : '0 8px 32px rgba(0,0,0,0.35)',
          border: isLight
            ? '1px solid rgba(0,0,0,0.06)'
            : '1px solid rgba(255,255,255,0.10)',
        }}
        aria-label="Main navigation"
      >
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.03em', color: isLight ? '#1B3A5C' : '#ffffff', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif" }}>
              Centric<span style={{ color: '#C1272D' }}>Q</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} className="hidden-mobile">
            <style>{`@media(max-width:900px){.hidden-mobile{display:none!important}}`}</style>
            {visibleLinks.map(([label, to]) => {
              const href    = (label === 'JD Prep' && user) ? '/jd-prep/generate' : to;
              const isRed   = label === 'JD Prep';
              const isActive = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={href}
                  className={`pub-nav-link ${isRed ? 'pub-nav-link-red' : isLight ? '' : 'pub-nav-link-dark'}`}
                  style={{
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 500,
                    padding: '6px 11px',
                    borderRadius: 8,
                    color: isRed ? '#C1272D' : isLight ? (isActive ? '#1B3A5C' : '#475569') : 'rgba(255,255,255,0.82)',
                    background: isActive && isLight ? 'rgba(27,58,92,0.06)' : 'transparent',
                    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Link>
              );
            })}
          </div>

          {/* Desktop right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="hidden-mobile">
            <Link to="/login"
              style={{ textDecoration: 'none', fontSize: 13, fontWeight: 500, padding: '6px 12px', borderRadius: 8, color: isLight ? '#475569' : 'rgba(255,255,255,0.75)', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif" }}
              className={`pub-nav-link ${isLight ? '' : 'pub-nav-link-dark'}`}
            >
              Sign in
            </Link>

            <Link to="/company/login"
              className={isLight ? 'pub-outline-btn' : 'pub-outline-btn-dark'}
              style={{
                textDecoration: 'none', fontSize: 13, fontWeight: 600,
                padding: '7px 14px', borderRadius: 9,
                border: isLight ? '1.5px solid #CBD5E1' : '1.5px solid rgba(255,255,255,0.25)',
                color: isLight ? '#1B3A5C' : 'rgba(255,255,255,0.85)',
                fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
                transition: 'background 0.15s',
              }}
            >
              For Companies
            </Link>

            <Link to="/register"
              className="pub-cta-btn"
              style={{
                textDecoration: 'none', fontSize: 13, fontWeight: 700,
                padding: '8px 18px', borderRadius: 9,
                background: '#C1272D', color: '#ffffff',
                fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
              }}
            >
              Start free
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: isLight ? '#1B3A5C' : '#ffffff', transition: 'background 0.15s' }}
            className="show-mobile"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <style>{`@media(max-width:900px){.show-mobile{display:flex!important;align-items:center;justify-content:center}}`}</style>
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

      </nav>

      {/* ── Mobile dropdown (rendered outside <nav> so z-index is clean) ── */}
      {menuOpen && (
        <>
          {/* Backdrop — dims the page, click to close */}
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
            aria-hidden="true"
          />

          {/* Dropdown panel — sits just below the navbar */}
          <div
            style={{
              position: 'fixed',
              top: isLight ? 60 : 72,
              left: 0, right: 0,
              zIndex: 99,
              background: '#ffffff',
              boxShadow: '0 12px 40px rgba(0,0,0,0.20)',
              borderTop: '1px solid #E2E8F0',
              maxHeight: 'calc(100svh - 80px)',
              overflowY: 'auto',
              fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
              animation: 'slideDown 0.2s ease',
            }}
          >
            {/* Nav links — simple vertical list */}
            <div style={{ padding: '6px 0' }}>
              {visibleLinks.map(([label, to]) => {
                const href    = (label === 'JD Prep' && user) ? '/jd-prep/generate' : to;
                const isRed   = label === 'JD Prep';
                const isActive = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={href}
                    style={{
                      textDecoration: 'none',
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 20px',
                      fontSize: 15, fontWeight: isActive ? 700 : 500,
                      color: isRed ? '#C1272D' : isActive ? '#1B3A5C' : '#334155',
                      background: isActive ? '#F0F6FF' : 'transparent',
                      borderLeft: isActive ? '3px solid #1B3A5C' : isRed ? '3px solid #C1272D' : '3px solid transparent',
                      transition: 'background 0.12s',
                      minHeight: 48,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive ? '#F0F6FF' : 'transparent'; }}
                  >
                    <span style={{ color: isRed ? '#C1272D' : isActive ? '#1B3A5C' : '#94A3B8', flexShrink: 0 }}>{LINK_ICONS[label]}</span>
                    {label}
                    {isRed && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: '#FEE2E2', color: '#C1272D', padding: '2px 8px', borderRadius: 99 }}>AI</span>}
                  </Link>
                );
              })}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#F1F5F9', margin: '4px 0' }} />

            {/* Sign in */}
            <Link to="/login"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', fontSize: 15, fontWeight: 500, color: '#475569', minHeight: 48, background: 'transparent', borderLeft: '3px solid transparent', transition: 'background 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Sign in
            </Link>

            {/* Divider */}
            <div style={{ height: 1, background: '#F1F5F9' }} />

            {/* CTA buttons */}
            <div style={{ padding: '14px 16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/register"
                style={{ display: 'block', textAlign: 'center', background: '#C1272D', color: '#fff', borderRadius: 12, padding: '14px 20px', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#A8202B'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#C1272D'; }}
              >
                Start free — no card needed
              </Link>
              <Link to="/company/login"
                style={{ display: 'block', textAlign: 'center', border: '1.5px solid #CBD5E1', color: '#1B3A5C', borderRadius: 12, padding: '13px 20px', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F0F6FF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                For Companies →
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Spacer: light pages need 60px to push content below the fixed bar; dark-hero pages start at y=0 — the section handles its own top padding */}
      <div style={{ height: isLight ? 60 : 0 }} aria-hidden="true" />
    </>
  );
}
